import type { AggregateOffer, UnitPriceSpecification } from 'apps/commerce/types.ts'

// ---------- Config ----------
const EPS = 0.01
const MIN_INSTALLMENT_AMOUNT = 0 // valor mínimo por parcela (R$)

type PreferMethod = 'auto' | 'card' | 'pix'
type UseOfferOptions = { preferMethod?: PreferMethod }

// ---------- Helpers ----------
const norm = (v?: string | number | null) =>
	String(v ?? '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()

const inAny = (hay: Array<string | number | null>, needle: string) =>
	hay.some((v) => norm(v).includes(needle))

const isInstallment = (s: UnitPriceSpecification) =>
	s.priceComponentType === 'https://schema.org/Installment'

const isPixSpec = (s: UnitPriceSpecification) => {
	const name = (s as any)?.name
	const pm = (s as any)?.paymentMethod
	const pct = (s as any)?.priceComponentType
	return (
		inAny([name, pm, pct], 'pix') ||
		inAny([name], 'a vista no pix') ||
		inAny([name], 'avista no pix')
	)
}

const isCardSpec = (s: UnitPriceSpecification) => {
	const name = (s as any)?.name
	const pm = (s as any)?.paymentMethod
	return (
		inAny([name, pm], 'cartao') ||
		inAny([name, pm], 'cartão') ||
		inAny([name, pm], 'credito') ||
		inAny([name, pm], 'crédito') ||
		inAny([name, pm], 'visa') ||
		inAny([name, pm], 'master') ||
		inAny([name, pm], 'mastercard') ||
		inAny([name, pm], 'elo') ||
		inAny([name, pm], 'amex') ||
		inAny([name, pm], 'hiper') ||
		inAny([name, pm], 'hipercard') ||
		inAny([name, pm], 'diners')
	)
}

const isListPriceSpec = (s: UnitPriceSpecification) =>
	s.priceType === 'https://schema.org/ListPrice' ||
	norm((s as any)?.priceType).includes('list') ||
	norm((s as any)?.name).includes('list')

const isSaleOrUnitPriceSpec = (s: UnitPriceSpecification) => {
	const pt = norm((s as any)?.priceType)
	const pct = norm((s as any)?.priceComponentType)
	// captura "SalePrice", "UnitPrice" e specs-base sem componentType
	return pt.includes('sale') || pt.includes('unit') || (!pct || pct === 'price')
}

// ---------- Installments ----------
type InstallmentView = {
	count: number
	amount: number
	total: number
	withTaxes: boolean
	raw: UnitPriceSpecification
}

const mapToInstallments = (
	specs: UnitPriceSpecification[] | undefined,
	sellingPrice: number,
): InstallmentView[] =>
	(specs ?? [])
		.filter(isInstallment)
		.filter((s) => s.billingDuration && s.billingIncrement)
		.map((s) => {
			const count = s.billingDuration as number
			const amount = s.billingIncrement as number
			const total = (s.price as number) ?? count * amount
			const withTaxes = sellingPrice < ((s.price as number) ?? Infinity)
			return { count, amount, total, withTaxes, raw: s }
		})

/**
 * 1) Filtra parcelas com valor >= minAmount (ou usa todas).
 * 2) Prioriza SEM JUROS (total <= noInterestRefPrice + EPS) e MAIOR número de parcelas (desempate: menor total).
 * 3) Se não houver sem juros, escolhe a MENOR TOTAL (desempate: maior número de parcelas).
 */
const pickBestInstallment = (
	all: InstallmentView[],
	noInterestRefPrice: number,
	minAmount: number,
): InstallmentView | null => {
	if (!all.length) return null

	const meetsMin = all.filter((i) => i.amount + EPS >= minAmount)
	const baseSet = meetsMin.length ? meetsMin : all

	const noInterest = baseSet.filter((i) => i.total <= noInterestRefPrice + EPS)

	if (noInterest.length) {
		return [...noInterest].sort((a, b) => b.count - a.count || a.total - b.total)[0]
	}

	return [...baseSet].sort((a, b) => a.total - b.total || b.count - a.count)[0] ?? null
}

export const bestInstallment = (
	acc: UnitPriceSpecification | null,
	curr: UnitPriceSpecification,
) => {
	if (curr.priceComponentType !== 'https://schema.org/Installment') return acc
	if (!acc) return curr
	if ((acc.price ?? Infinity) > (curr.price ?? Infinity)) return curr
	if ((acc.price ?? Infinity) < (curr.price ?? Infinity)) return acc
	if (acc.billingDuration && curr.billingDuration && acc.billingDuration < curr.billingDuration) {
		return curr
	}
	return acc
}

export const installmentToString = (
	installment: UnitPriceSpecification,
	sellingPrice: number,
) => {
	const { billingDuration, billingIncrement, price } = installment
	if (!billingDuration || !billingIncrement) return ''
	const withTaxes = sellingPrice < ((price as number) ?? Infinity)
	return `${billingDuration}x de R$ ${billingIncrement} ${withTaxes ? 'com juros' : 'sem juros'}`
}

const getInstallmentsInfo = (
	installment: UnitPriceSpecification,
	sellingPrice: number,
) => {
	const { billingDuration, billingIncrement, price } = installment
	if (!billingDuration || !billingIncrement) return null
	const withTaxes = sellingPrice < ((price as number) ?? Infinity)
	return { billingDuration, billingIncrement, withTaxes }
}

// ---------- ListPrice (global) ----------
const getGlobalListPrice = (
	specs: UnitPriceSpecification[] | undefined,
	offer?: AggregateOffer['offers'][0],
) => {
	const listSpecs = (specs ?? []).filter(isListPriceSpec)
	const maxList = listSpecs.reduce<number | null>((acc, s) => {
		const p = typeof s.price === 'number' ? s.price : null
		if (p == null) return acc
		return acc == null ? p : Math.max(acc, p)
	}, null)

	if (typeof maxList === 'number') return maxList

	if (typeof offer?.price === 'number' && typeof offer?.highPrice === 'number') {
		return offer.highPrice > offer.price ? offer.highPrice : null
	}
	return null
}

// ---------- Preços do CARTÃO (estritos) ----------
const getCardPricesStrict = (specs: UnitPriceSpecification[] | undefined) => {
	const cardSpecs = (specs ?? []).filter(isCardSpec)

	const cardListPrice = cardSpecs
		.filter(isListPriceSpec)
		.reduce<number | null>((acc, s) => {
			const p = typeof s.price === 'number' ? s.price : null
			return p == null ? acc : (acc == null ? p : Math.max(acc, p))
		}, null)

	const cardPrice = cardSpecs
		.filter((s) => !isInstallment(s) && !isPixSpec(s))
		.filter(isSaleOrUnitPriceSpec)
		.reduce<number | null>((acc, s) => {
			const p = typeof s.price === 'number' ? s.price : null
			return p == null ? acc : (acc == null ? p : Math.min(acc, p))
		}, null)

	return { cardListPrice, cardPrice }
}

// ---------- Fallback seguro NÃO-PIX ----------
const getNonPixBasePrices = (specs: UnitPriceSpecification[] | undefined) => {
	const nonPixBase = (specs ?? []).filter((s) => !isPixSpec(s) && !isInstallment(s))
	const list = nonPixBase
		.filter(isListPriceSpec)
		.reduce<number | null>((acc, s) => {
			const p = typeof s.price === 'number' ? s.price : null
			return p == null ? acc : (acc == null ? p : Math.max(acc, p))
		}, null)

	const sale = nonPixBase
		.filter(isSaleOrUnitPriceSpec)
		.reduce<number | null>((acc, s) => {
			const p = typeof s.price === 'number' ? s.price : null
			return p == null ? acc : (acc == null ? p : Math.min(acc, p))
		}, null)

	return { list, sale }
}

type UseOfferReturn = {
	price?: number
	listPrice?: number
	availability?: string
	seller?: string

	installments: string | null
	installmentsInfo:
		| { billingDuration: number; billingIncrement: number; withTaxes: boolean }
		| null
	pureInstallments?: UnitPriceSpecification
	withTaxes: boolean
	hasInstallments: boolean

	pixPrice?: number
	cardTotal?: number

	cardPrice?: number
	cardListPrice?: number

	displayList?: number
	displayPrice?: number

	methods: UnitPriceSpecification[]
}

export const useOffer = (
	aggregateOffer?: AggregateOffer,
	opts: UseOfferOptions = { preferMethod: 'card' },
): UseOfferReturn => {
	const preferMethod: PreferMethod = opts?.preferMethod ?? 'card'

	const offer = aggregateOffer?.offers?.[0]
	const specs = offer?.priceSpecification ?? []

	const seller = offer?.seller
	const price = offer?.price
	const availability = offer?.availability
	const methods = specs

	const listPrice = getGlobalListPrice(specs, offer)
	const pixPrice = specs.find(isPixSpec)?.price

	// Installments (para cartão)
	const installmentsAll = mapToInstallments(specs, price ?? Infinity)
	const installmentsCard = mapToInstallments(
		(specs ?? []).filter(isCardSpec),
		price ?? Infinity,
	)
	const installmentsBase = installmentsCard.length ? installmentsCard : installmentsAll
	const noInterestRefPrice = typeof listPrice === 'number' ? listPrice : (price ?? Infinity)
	const picked = pickBestInstallment(installmentsBase, noInterestRefPrice, MIN_INSTALLMENT_AMOUNT)

	// ---- NOVA REGRA: permite 1x APENAS se >= R$ 30; sempre exige parcela >= R$ 30
	const isValidInstallment = !!picked && (picked.count ?? 0) >= 1 &&
		(picked.amount ?? 0) + EPS >= MIN_INSTALLMENT_AMOUNT

	const installment = isValidInstallment ? picked!.raw : null
	const cardTotal = isValidInstallment ? picked!.total : null

	// Preços de cartão (estritos, sem fallback potencialmente Pix)
	let { cardListPrice, cardPrice } = getCardPricesStrict(specs)

	// Fallback NÃO-PIX se ainda vazio
	if (cardPrice == null || cardListPrice == null) {
		const nonPix = getNonPixBasePrices(specs)
		if (cardPrice == null && typeof nonPix.sale === 'number') cardPrice = nonPix.sale
		if (cardListPrice == null && typeof nonPix.list === 'number') cardListPrice = nonPix.list
	}

	// Último fallback: offer.price somente se NÃO for claramente Pix
	if (cardPrice == null && typeof price === 'number' && price !== pixPrice) {
		cardPrice = price
	}
	if (cardListPrice == null && typeof listPrice === 'number') {
		cardListPrice = listPrice
	}

	// display: por padrão SEMPRE cartão
	let displayList = cardListPrice ?? undefined
	let displayPrice = cardPrice ?? undefined

	if (preferMethod === 'pix') {
		displayList = (listPrice ?? cardListPrice) ?? undefined
		displayPrice = (typeof pixPrice === 'number' ? pixPrice : price ?? cardPrice) ?? undefined
	} else if (preferMethod === 'auto') {
		displayList = (cardListPrice ?? listPrice) ?? undefined
		// evita cair em Pix
		displayPrice = (cardPrice ?? (price !== pixPrice ? price : undefined)) ?? undefined
	}

	return {
		price,
		listPrice: typeof listPrice === 'number' ? listPrice : undefined,
		availability,
		seller,

		installments: installment && price ? installmentToString(installment, price) : null,
		installmentsInfo: installment && price ? getInstallmentsInfo(installment, price) : null,
		pureInstallments: installment ?? undefined,
		withTaxes: installment
			? (price ?? Infinity) < ((installment?.price as number) ?? Infinity)
			: false,
		hasInstallments: !!installment,

		pixPrice: typeof pixPrice === 'number' ? pixPrice : undefined,
		cardTotal: typeof cardTotal === 'number' ? cardTotal : undefined,

		cardPrice: typeof cardPrice === 'number' ? cardPrice : undefined,
		cardListPrice: typeof cardListPrice === 'number' ? cardListPrice : undefined,

		displayList: typeof displayList === 'number' ? displayList : undefined,
		displayPrice: typeof displayPrice === 'number' ? displayPrice : undefined,

		methods,
	}
}
